import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select } from 'antd';
import {
  Search,
  RefreshCw,
  Users,
  Award,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { assessmentApi } from '../services/assessmentApi';
import {
  ApiDayItem,
  AssessmentDashboardResponse,
  AssessmentDashboardStudentItem,
} from '../data/types';
import { API_URL_IMAGE } from 'env';

import { DAYS_META } from '../data/questionsData';

// Colors & styles for classifications matching Foundation Checkpoint design
export const CLS_CONFIG: Record<
  string,
  { label: string; fg: string; bg: string; dot: string; border: string }
> = {
  GREEN: {
    label: 'XANH',
    fg: '#1E7A4E',
    bg: '#E3F3EA',
    dot: '#2E9E66',
    border: '#A3E6CD',
  },
  YELLOW: {
    label: 'VÀNG',
    fg: '#8A6400',
    bg: '#FBF1D6',
    dot: '#D9A21B',
    border: '#FCE5A3',
  },
  ORANGE: {
    label: 'CAM',
    fg: '#A4510F',
    bg: '#FCE9DA',
    dot: '#E07B2A',
    border: '#F7C6A5',
  },
  RED: {
    label: 'ĐỎ',
    fg: '#B42A2A',
    bg: '#FBE3E3',
    dot: '#D14343',
    border: '#F8BDBD',
  },
  UNCLASSIFIED: {
    label: 'CHƯA PHÂN LOẠI',
    fg: '#5A6577',
    bg: '#EEF1F4',
    dot: '#8A94A3',
    border: '#D5DBE3',
  },
  NONE: {
    label: '—',
    fg: '#8A94A3',
    bg: '#EEF1F4',
    dot: '#C3CAD4',
    border: '#E2E6EC',
  },
};

const pad = (n: number) => String(n).padStart(2, '0');
const fmtDT = (iso?: string | null) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
      d.getHours()
    )}:${pad(d.getMinutes())}`;
  } catch {
    return iso || '—';
  }
};

const getInitials = (name?: string) => {
  if (!name) return 'HV';
  return (
    name
      .trim()
      .split(' ')
      .filter(Boolean)
      .slice(-2)
      .map((w) => w[0])
      .join('')
      .toUpperCase() || 'HV'
  );
};

/**
 * Kiểm tra xem user hiện tại trong localStorage có role là 'admin' hoặc 'super_admin' hay không
 */
export function isUserAdmin(): boolean {
  try {
    const raw = localStorage.getItem('user');
    if (!raw) return false;
    const user = JSON.parse(raw);
    if (!user) return false;

    // 1. Kiểm tra mảng roles: [{ id: 1, name: "super_admin", ... }]
    if (Array.isArray(user.roles)) {
      const hasAdmin = user.roles.some((r: any) => {
        const name = String(r?.name || '').toLowerCase();
        return name === 'admin' || name === 'super_admin';
      });
      if (hasAdmin) return true;
    }

    // 2. Kiểm tra mảng assigned_roles: [{ id: 1, name: "super_admin", ... }]
    if (Array.isArray(user.assigned_roles)) {
      const hasAdminAssigned = user.assigned_roles.some((r: any) => {
        const name = String(r?.name || '').toLowerCase();
        return name === 'admin' || name === 'super_admin';
      });
      if (hasAdminAssigned) return true;
    }

    // 3. Kiểm tra mảng role_names: ["User", "Super Administrator"]
    if (Array.isArray(user.role_names)) {
      const hasAdminName = user.role_names.some((r: any) => {
        const name = String(r || '').toLowerCase();
        return (
          name.includes('super admin') ||
          name.includes('admin') ||
          name.includes('administrator')
        );
      });
      if (hasAdminName) return true;
    }

    // 4. Kiểm tra chuỗi role đơn (nếu có): user.role
    if (typeof user.role === 'string') {
      const r = user.role.toLowerCase();
      if (r === 'admin' || r === 'super_admin') return true;
    }

    // 5. Kiểm tra roles_display: "User, Super Administrator"
    if (typeof user.roles_display === 'string') {
      const rd = user.roles_display.toLowerCase();
      if (rd.includes('admin') || rd.includes('super_admin') || rd.includes('administrator')) return true;
    }

    return false;
  } catch {
    return false;
  }
}

// Score bands of the backend classification, as a share of the maximum score of what the student answered
const CLS_BANDS: Array<{ key: string; range: string }> = [
  { key: 'GREEN', range: '≥ 85%' },
  { key: 'YELLOW', range: '70–84%' },
  { key: 'ORANGE', range: '55–69%' },
  { key: 'RED', range: '< 55%' },
];

const StudentAvatar: React.FC<{ url: string | null; name: string }> = ({ url, name }) => {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return <img src={url} alt={name} className="fc-student-avatar" onError={() => setFailed(true)} />;
  }
  return <div className="fc-student-initials">{getInitials(name)}</div>;
};

interface MentorAssessmentDashboardProps {
  apiDays?: ApiDayItem[];
}

export const MentorAssessmentDashboard: React.FC<MentorAssessmentDashboardProps> = ({ apiDays }) => {
  const isAdmin = isUserAdmin();

  const [data, setData] = useState<AssessmentDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [activeCls, setActiveCls] = useState<string>('ALL');
  const [selectedDay, setSelectedDay] = useState<number | 'ALL'>('ALL');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasChecked, setHasChecked] = useState<boolean>(false);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const dayOptions = React.useMemo(() => {
    if (Array.isArray(apiDays) && apiDays.length > 0) {
      return apiDays.map((d) => ({
        day: Number(d.day_number || d.id),
      }));
    }
    return DAYS_META.map((m) => ({ day: m.day }));
  }, [apiDays]);

  // Fetch dashboard data from API
  const fetchDashboard = useCallback(
    async (
      searchQuery: string = search,
      clsFilter: string = activeCls,
      page: number = currentPage,
      dayFilter: number | 'ALL' = selectedDay,
      showRefreshIndicator: boolean = false
    ) => {
      // Chỉ user có role admin hoặc super_admin mới được gọi API
      if (!isUserAdmin()) {
        setData(null);
        setIsLoading(false);
        setHasChecked(true);
        return;
      }

      try {
        if (showRefreshIndicator) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }

        const res = await assessmentApi.getDashboard({
          search: searchQuery || undefined,
          classification:
            clsFilter !== 'ALL' ? [clsFilter] : undefined,
          page,
          day_id: dayFilter !== 'ALL' ? dayFilter : undefined,
        });

        // Validate response structure
        if (res && res.stats && res.students) {
          setData(res);
          setLoadFailed(false);
        } else {
          setData(null);
        }
      } catch (err: any) {
        console.warn('Assessment dashboard not available or unauthorized:', err);
        const status = err?.response?.status;
        // 401 / 403: this admin has no access, hide the section. Anything else (timeout, network): offer a retry
        if (status === 401 || status === 403) {
          setData(null);
        } else {
          setLoadFailed(true);
        }
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
        setHasChecked(true);
      }
    },
    [search, activeCls, currentPage, selectedDay]
  );

  // Initial load and filter effect
  useEffect(() => {
    if (isAdmin) {
      fetchDashboard(search, activeCls, currentPage, selectedDay);
    }
  }, [fetchDashboard, search, activeCls, currentPage, selectedDay, isAdmin]);

  // Search as the mentor types (no need to press Enter)
  useEffect(() => {
    const next = searchInput.trim();
    if (next === search) return;
    const timer = window.setTimeout(() => {
      setCurrentPage(1);
      setSearch(next);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput, search]);

  // Handle search submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(searchInput.trim());
  };

  // Handle classification filter change
  const handleClsChange = (cls: string) => {
    setCurrentPage(1);
    setActiveCls(cls);
  };

  // Handle day filter change
  const handleDayChange = (day: number | 'ALL') => {
    setCurrentPage(1);
    setSelectedDay(day);
  };

  const goToPage = (page: number) => {
    setCurrentPage(page);
    panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // 1. Chỉ hiện khi user trong localStorage có role là admin hoặc super_admin
  if (!isAdmin) {
    return null;
  }

  // 2. Tải lỗi (mạng / timeout) khi chưa có dữ liệu: cho phép thử lại thay vì ẩn mất cả mục
  if (hasChecked && !data && loadFailed) {
    return (
      <section className="fc-mentor-dash" id="mentor-assessment-dashboard">
        <div className="fc-mentor-error">
          <AlertTriangle size={16} />
          <span>Chưa tải được bảng điều khiển đánh giá học viên.</span>
          <button
            type="button"
            className={`fc-mentor-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
            onClick={() => fetchDashboard(search, activeCls, currentPage, selectedDay, true)}
          >
            <RefreshCw size={15} />
            <span>Thử lại</span>
          </button>
        </div>
      </section>
    );
  }

  // 3. Nếu đã kiểm tra xong mà không có dữ liệu, ẩn hoàn toàn
  if (hasChecked && !data) {
    return null;
  }

  // First load can take several seconds: show where the section will appear instead of nothing
  if (!hasChecked && isLoading) {
    return (
      <section className="fc-mentor-dash" id="mentor-assessment-dashboard" aria-busy="true">
        <div className="fc-mentor-error is-loading">
          <RefreshCw size={16} className="spinning" />
          <span>Đang tải bảng điều khiển đánh giá học viên...</span>
        </div>
      </section>
    );
  }

  // If data exists, render the mentor dashboard section
  if (!data) {
    return null;
  }

  const { stats, students } = data;
  const studentList = students?.data || [];
  const totalStudents = students?.total || 0;
  const lastPage = students?.last_page || 1;
  const fromRow = students?.from ?? (studentList.length ? 1 : 0);
  const toRow = students?.to ?? studentList.length;

  // With a day selected, total_questions is that day's count, so its maximum score is meaningful.
  // Across all days the backend scores each student on the days they took, not on every question,
  // so the table shows percentages instead of "x / 240".
  const isDayView = selectedDay !== 'ALL';
  const dayQuestions = Number(stats?.total_questions || 0);
  const dayMaxScore = dayQuestions * 5;

  const clsCount = (ratio?: number) => Math.round(((ratio ?? 0) / 100) * (stats.total_students || 0));
  const distribution = CLS_BANDS.map((b) => {
    const ratio =
      b.key === 'GREEN' ? stats.ratio_green :
      b.key === 'YELLOW' ? stats.ratio_yellow :
      b.key === 'ORANGE' ? stats.ratio_orange : stats.ratio_red;
    return { ...b, ratio: Number(ratio ?? 0), count: clsCount(ratio), cfg: CLS_CONFIG[b.key] };
  });

  // Filter chips definitions
  const filterChips: Array<{ key: string; label: string; config?: any }> = [
    { key: 'ALL', label: 'Tất cả' },
    { key: 'GREEN', label: 'Xanh', config: CLS_CONFIG.GREEN },
    { key: 'YELLOW', label: 'Vàng', config: CLS_CONFIG.YELLOW },
    { key: 'ORANGE', label: 'Cam', config: CLS_CONFIG.ORANGE },
    { key: 'RED', label: 'Đỏ', config: CLS_CONFIG.RED },
    { key: 'UNCLASSIFIED', label: 'Chưa phân loại', config: CLS_CONFIG.UNCLASSIFIED },
  ];

  return (
    <section className="fc-mentor-dash" id="mentor-assessment-dashboard">
      {/* Section Header */}
      <div className="fc-mentor-header">
        <div className="fc-mentor-header-info">
          <div className="fc-mentor-scope">
            <span className="fc-mentor-badge">
              TOÀN HỆ THỐNG · ADMIN & MENTOR{isDayView ? ` · NGÀY ${selectedDay}` : ''}
            </span>
          </div>
          <h2 className="fc-mentor-title">Bảng điều khiển Đánh giá Học viên</h2>
          <p className="fc-mentor-sub">
            Theo dõi tiến độ, phân loại năng lực và kết quả đánh giá của học viên
          </p>
        </div>

        <div className="fc-mentor-actions">
          <div className="fc-mentor-day-filter">
            <span className="fc-mentor-day-label">Ngày đánh giá:</span>
            <Select
              className="fc-mentor-day-select"
              value={selectedDay}
              onChange={(val) => handleDayChange(val as number | 'ALL')}
              style={{ width: 120 }}
              options={[
                { value: 'ALL', label: 'Tất cả' },
                ...dayOptions.map((m) => ({
                  value: m.day,
                  label: `Ngày ${m.day}`,
                })),
              ]}
            />
          </div>

          <button
            type="button"
            className={`fc-mentor-refresh-btn ${isRefreshing ? 'spinning' : ''}`}
            onClick={() => fetchDashboard(search, activeCls, currentPage, selectedDay, true)}
            title="Làm mới dữ liệu"
          >
            <RefreshCw size={15} />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* KPIs: three headline numbers and the classification spread */}
      <div className="fc-mentor-kpi-grid">
        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span className="fc-kpi-icon-wrap">
              <Users size={14} />
            </span>
            <span className="fc-kpi-label">Số học viên</span>
          </div>
          <div className="fc-kpi-val mono-font">{stats.total_students}</div>
          <div className="fc-kpi-sub">{isDayView ? `đã làm Ngày ${selectedDay}` : 'đã tham gia đánh giá'}</div>
        </div>

        <div className="fc-kpi-card">
          <div className="fc-kpi-top">
            <span className="fc-kpi-icon-wrap">
              <Award size={14} />
            </span>
            <span className="fc-kpi-label">Điểm TB</span>
          </div>
          <div className="fc-kpi-val mono-font">
            {stats.avg_total_score != null ? stats.avg_total_score : '—'}
            {isDayView && dayMaxScore > 0 && <small>/{dayMaxScore}</small>}
          </div>
          <div className="fc-kpi-sub">{isDayView ? 'điểm / học viên' : 'tổng điểm / học viên'}</div>
        </div>

        <button
          type="button"
          className={`fc-kpi-card highlight-danger${stats.need_review ? ' is-alert' : ''}`}
          onClick={() => handleClsChange('RED')}
          title="Lọc nhóm Đỏ"
        >
          <div className="fc-kpi-top">
            <AlertTriangle size={13} color={CLS_CONFIG.RED.dot} />
            <span className="fc-kpi-label">Cần đánh giá lại</span>
          </div>
          <div className="fc-kpi-val mono-font" style={{ color: CLS_CONFIG.RED.fg }}>
            {stats.need_review ?? 0}
          </div>
          <div className="fc-kpi-sub">ưu tiên đánh giá 1:1</div>
        </button>

        <div className="fc-kpi-card fc-kpi-dist">
          <div className="fc-kpi-top">
            <span className="fc-kpi-label">Phân loại năng lực</span>
            <span className="fc-kpi-hint">theo % điểm</span>
          </div>
          <div className="fc-dist-bar" aria-hidden="true">
            {distribution.filter((d) => d.ratio > 0).map((d) => (
              <span key={d.key} style={{ width: `${d.ratio}%`, background: d.cfg.dot }} />
            ))}
          </div>
          <div className="fc-dist-legend">
            {distribution.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`fc-dist-item${activeCls === d.key ? ' active' : ''}`}
                onClick={() => handleClsChange(activeCls === d.key ? 'ALL' : d.key)}
                title={`Lọc nhóm ${d.cfg.label}`}
              >
                <span className="fc-chip-dot" style={{ background: d.cfg.dot }} />
                <span className="fc-dist-name">{d.cfg.label}</span>
                <span className="fc-dist-val mono-font" style={{ color: d.cfg.fg }}>{d.ratio}%</span>
                <span className="fc-dist-range">{d.count} HV · {d.range}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="fc-mentor-panel" ref={panelRef}>
        {/* Controls: Search, Classification Chips, Count */}
        <div className="fc-mentor-controls">
          {/* Search bar */}
          <form className="fc-mentor-search-form" onSubmit={handleSearchSubmit}>
            <Search size={16} className="fc-search-icon" />
            <input
              type="search"
              placeholder="Tìm theo tên, email, chức danh..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="fc-mentor-search-input"
            />
            {searchInput && (
              <button
                type="button"
                className="fc-search-clear-btn"
                aria-label="Xóa tìm kiếm"
                onClick={() => {
                  setSearchInput('');
                  setSearch('');
                  setCurrentPage(1);
                }}
              >
                ✕
              </button>
            )}
          </form>

          {/* Classification chips */}
          <div className="fc-mentor-chips">
            {filterChips.map((chip) => {
              const isActive = activeCls === chip.key;
              const cfg = chip.config;
              const bg = isActive
                ? chip.key === 'ALL'
                  ? '#0E1A2D'
                  : cfg?.bg || '#E2E6EC'
                : '#FFFFFF';
              const fg = isActive
                ? chip.key === 'ALL'
                  ? '#FFFFFF'
                  : cfg?.fg || '#0E1A2D'
                : '#475569';
              const border = isActive
                ? chip.key === 'ALL'
                  ? '#0E1A2D'
                  : cfg?.border || '#CBD5E1'
                : '#E2E8F0';

              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`fc-chip-btn ${isActive ? 'active' : ''}`}
                  onClick={() => handleClsChange(chip.key)}
                  style={{ background: bg, color: fg, borderColor: border }}
                >
                  {cfg && (
                    <span
                      className="fc-chip-dot"
                      style={{ background: cfg.dot }}
                    />
                  )}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>

          {/* Count Badge */}
          <div className="fc-mentor-count-badge">
            {totalStudents > 0 ? (
              <>
                <span className="mono-font">{fromRow}–{toRow}</span> / {totalStudents} học viên
              </>
            ) : (
              <>0 học viên</>
            )}
          </div>
        </div>

        {/* Data Table (cards on phones) */}
        <div className={`fc-table-responsive${isLoading ? ' is-loading' : ''}`}>
          <table className="fc-mentor-table">
            <thead>
              <tr>
                <th>Học viên</th>
                <th className="ta-c" style={{ width: 150 }}>Hoàn thành</th>
                <th className="ta-c" style={{ width: 120 }}>Điểm</th>
                <th className="ta-c" style={{ width: 120 }}>Phân loại</th>
                <th className="ta-c" style={{ width: 112 }}>Mentor</th>
                <th className="ta-r col-updated" style={{ width: 148 }}>Cập nhật</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && studentList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fc-table-loading-td">
                    <div className="fc-table-loading-box">
                      <RefreshCw size={20} className="spinning" />
                      <span>Đang nạp danh sách học viên...</span>
                    </div>
                  </td>
                </tr>
              ) : studentList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fc-table-empty-td">
                    <div className="fc-table-empty-box">
                      <Filter size={24} />
                      <div className="fc-empty-title">Không tìm thấy học viên phù hợp</div>
                      <div className="fc-empty-sub">
                        Vui lòng thử tìm kiếm với từ khóa khác hoặc thay đổi bộ lọc phân loại.
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                studentList.map((item) => {
                  const u = item.user;
                  const name = u?.fullname || `Học viên #${item.user_id}`;
                  const email = u?.email || '—';
                  const job = u?.job_title;
                  const avatarUrl = u?.avatar
                    ? u.avatar.startsWith('http')
                      ? u.avatar
                      : `${API_URL_IMAGE}/${u.avatar}`
                    : null;
                  const clsCode = String(item.classification || 'UNCLASSIFIED').toUpperCase();
                  const clsInfo = CLS_CONFIG[clsCode] || CLS_CONFIG.UNCLASSIFIED;
                  const isFlagged = Number(item.flag_review_mentor) === 1;
                  // The backend can report more than 100% (answers counted across days): cap what we draw
                  const completionPct = Math.min(100, Math.max(0, parseFloat(String(item.completion_percentage || 0)) || 0));
                  const scorePct = Math.min(100, Math.max(0, parseFloat(String(item.score_percentage || 0)) || 0));
                  const answered = isDayView && dayQuestions > 0 ? Math.min(item.total_answered, dayQuestions) : item.total_answered;

                  return (
                    <tr key={item.id} className={`fc-student-row${isFlagged ? ' is-flagged' : ''}`}>
                      {/* Student info */}
                      <td className="cell-student">
                        <div className="fc-student-cell">
                          <StudentAvatar url={avatarUrl} name={name} />
                          <div className="fc-student-details">
                            <div className="fc-student-name" title={name}>
                              {name}
                            </div>
                            <div className="fc-student-meta" title={`${email}${job ? ` · ${job}` : ''}`}>
                              {email}
                              {job ? ` · ${job}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Answered & completion */}
                      <td className="ta-c" data-label="Hoàn thành">
                        <div className="fc-progress-cell">
                          <span className="fc-pct-text">
                            <b className="mono-font">{completionPct.toFixed(0)}%</b>
                            <span className="fc-stat-denom">
                              {answered}
                              {isDayView && dayQuestions > 0 ? `/${dayQuestions}` : ''} câu
                            </span>
                          </span>
                          <div className="fc-progress-track">
                            <div
                              className="fc-progress-fill"
                              style={{
                                width: `${completionPct}%`,
                                background: completionPct >= 100 ? '#10B981' : '#3B82F6',
                              }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Score */}
                      <td className="ta-c" data-label="Điểm">
                        <div className="fc-score-cell">
                          <span className="mono-font fc-score-bold">
                            {item.total_score}
                            {isDayView && dayMaxScore > 0 && <span className="fc-stat-denom">/{dayMaxScore}</span>}
                          </span>
                          <span className="mono-font fc-score-pct">{scorePct.toFixed(1)}%</span>
                        </div>
                      </td>

                      {/* Classification Badge */}
                      <td className="ta-c" data-label="Phân loại">
                        <span
                          className="fc-cls-badge"
                          style={{
                            background: clsInfo.bg,
                            color: clsInfo.fg,
                            borderColor: clsInfo.border,
                          }}
                        >
                          <span
                            className="fc-cls-dot"
                            style={{ background: clsInfo.dot }}
                          />
                          {clsInfo.label}
                        </span>
                      </td>

                      {/* Mentor Review Flag */}
                      <td className="ta-c cell-flag" data-label="Mentor">
                        {isFlagged ? (
                          <span className="fc-review-flag active" title="Yêu cầu Mentor đánh giá 1:1">
                            <AlertTriangle size={13} />
                            <span>Cần đánh giá</span>
                          </span>
                        ) : (
                          <span className="fc-review-flag normal">
                            <span>—</span>
                          </span>
                        )}
                      </td>

                      {/* Updated At */}
                      <td className="ta-r col-updated" data-label="Cập nhật">
                        <span className="fc-updated-at mono-font">
                          {fmtDT(item.updated_at || item.created_at)}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {lastPage > 1 && (
          <div className="fc-mentor-pagination">
            <div className="fc-page-info">
              Trang <span className="mono-font">{currentPage}</span> / <span className="mono-font">{lastPage}</span>
            </div>

            <div className="fc-page-nav">
              <button
                type="button"
                className="fc-page-btn"
                disabled={currentPage <= 1 || isLoading}
                onClick={() => goToPage(Math.max(1, currentPage - 1))}
              >
                <ChevronLeft size={16} />
                <span>Trước</span>
              </button>

              <div className="fc-page-numbers">
                {Array.from({ length: lastPage }, (_, i) => i + 1)
                  .filter((p) => {
                    return (
                      p === 1 ||
                      p === lastPage ||
                      Math.abs(p - currentPage) <= 1
                    );
                  })
                  .map((p, idx, arr) => {
                    const prev = arr[idx - 1];
                    const hasGap = prev && p - prev > 1;
                    return (
                      <React.Fragment key={p}>
                        {hasGap && <span className="fc-page-ellipsis">...</span>}
                        <button
                          type="button"
                          className={`fc-page-num ${p === currentPage ? 'active' : ''}`}
                          onClick={() => goToPage(p)}
                        >
                          {p}
                        </button>
                      </React.Fragment>
                    );
                  })}
              </div>

              <button
                type="button"
                className="fc-page-btn"
                disabled={currentPage >= lastPage || isLoading}
                onClick={() => goToPage(Math.min(lastPage, currentPage + 1))}
              >
                <span>Sau</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default MentorAssessmentDashboard;
